import React from 'react';
import { Box, Container, Paper, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { Construction as ConstructionIcon } from '@mui/icons-material';
import { ISpecialty } from '../../types/interfaces/ISpecialty';

interface UnderConstructionPageProps {
  title?: string;
  message?: string;
  specialty?: ISpecialty | null;
}

const UnderConstructionPage: React.FC<UnderConstructionPageProps> = ({
  title = 'Página en Construcción',
  message = 'Esta funcionalidad está siendo desarrollada y estará disponible próximamente.',
  specialty,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 200px)', // Altura del viewport menos header y sidebar
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.background.default,
        backgroundImage: `
          radial-gradient(circle at 25% 25%, ${theme.palette.primary.light}10 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, ${theme.palette.secondary.light}10 0%, transparent 50%)
        `,
        p: 2,
        mt: 2, // Margen superior para separar del header
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 6,
              borderRadius: 3,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              textAlign: 'center',
            }}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ConstructionIcon
                sx={{
                  fontSize: 120,
                  color: theme.palette.warning.main,
                  mb: 3,
                }}
              />
            </motion.div>

            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
                mb: 2,
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
              paragraph
              sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
            >
              {message}
            </Typography>

            {specialty && (
              <Box
                sx={{
                  p: 3,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  mb: 4,
                }}
              >
                <Typography variant="h6" gutterBottom color="primary">
                  Especialidad: {specialty?.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Esta especialidad está en desarrollo. Pronto tendrás acceso a:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    textAlign: 'left',
                    mt: 2,
                    pl: 3,
                    '& li': {
                      mb: 1,
                      color: 'text.secondary',
                    },
                  }}
                >
                  <li>Historias clínicas especializadas</li>
                  <li>Turnos y citas</li>
                  <li>Dashboard con estadísticas</li>
                  <li>Búsqueda de pacientes</li>
                </Box>
              </Box>
            )}

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 4, fontStyle: 'italic' }}
            >
              ¿Tienes alguna pregunta? Contacta al equipo de desarrollo.
            </Typography>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default UnderConstructionPage;
